import { useState, useRef, useCallback } from 'react';
import { API_BASE_URL } from '@/services/api/client';
import { TokenService } from '@/services/auth/TokenService';
import { readSSEStream } from '@/utils/sseStream';
import type { ConversationSituationDocument } from '@/types/conversation.types';
import { auditConversationSituation } from '@/services/api/conversations.api';

export type ConversationGenerationStatus = 'idle' | 'generating' | 'complete' | 'error';

export function useGenerateConversation() {
  const [status, setStatus] = useState<ConversationGenerationStatus>('idle');
  const [progressMessages, setProgressMessages] = useState<string[]>([]);
  const [generatedDocument, setGeneratedDocument] = useState<ConversationSituationDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [integrityErrors, setIntegrityErrors] = useState<string[]>([]);
  const [auditingDocument, setAuditingDocument] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const generate = useCallback(
    async (params: { level: string; context: string; studentRole?: string | null }) => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setStatus('generating');
      setProgressMessages([]);
      setGeneratedDocument(null);
      setError(null);
      setIntegrityErrors([]);

      const token = TokenService.getToken();

      try {
        const response = await fetch(`${API_BASE_URL}/backoffice/generate-conversation-situation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            level: params.level,
            context: params.context,
            studentRole: params.studentRole ?? null,
          }),
          signal: abortRef.current.signal,
        });

        if (!response.ok) {
          const data = (await response.json()) as { error?: string };
          throw new Error(data.error ?? `Error ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response stream available');

        await readSSEStream(reader, (event) => {
          if (event.type === 'progress' && event.message) {
            setProgressMessages((prev) => [...prev, event.message!]);
          } else if (event.type === 'complete' && event.document) {
            setGeneratedDocument(event.document as ConversationSituationDocument);
            setStatus('complete');
          } else if (event.type === 'error') {
            throw new Error(event.message ?? 'Error desconocido');
          }
        });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        const message = err instanceof Error ? err.message : 'Error desconocido';
        setError(message);
        setStatus('error');
      }
    },
    [],
  );

  /**
   * Pasada correctiva on-demand sobre el documento generado. Reaprovecha el
   * auditor de calidad del backend. Si el documento queda válido, sustituye
   * el draft; si quedan errores, se exponen para que el modal vuelva a
   * mostrarlos.
   */
  const retryIntegrityWithAI = useCallback(async () => {
    if (!generatedDocument) return;
    setAuditingDocument(true);
    try {
      const { document, errors } = await auditConversationSituation(generatedDocument);
      setGeneratedDocument(document);
      setIntegrityErrors(errors);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setIntegrityErrors((prev) => [`El reintento con IA falló: ${message}`, ...prev]);
    } finally {
      setAuditingDocument(false);
    }
  }, [generatedDocument]);

  /**
   * Permite a la página inyectar errores devueltos por el endpoint de
   * guardado cuando responde 400 con `details` (lista de strings).
   */
  const setIntegrityErrorsFromSave = useCallback((errors: string[]) => {
    setIntegrityErrors(errors);
  }, []);

  const clearIntegrityErrors = useCallback(() => setIntegrityErrors([]), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setStatus('idle');
    setProgressMessages([]);
    setGeneratedDocument(null);
    setError(null);
    setIntegrityErrors([]);
    setAuditingDocument(false);
  }, []);

  return {
    status,
    progressMessages,
    generatedDocument,
    error,
    integrityErrors,
    auditingDocument,
    generate,
    retryIntegrityWithAI,
    setIntegrityErrorsFromSave,
    clearIntegrityErrors,
    reset,
  };
}
